/**
 * Copyright 2024-2026, Optimizely
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { scheduleMicrotask } from "../microtask";
export class ExponentialBackoff {
    constructor(base, max, maxJitter) {
        this.base = base;
        this.max = max;
        this.maxJitter = maxJitter;
        if (this.maxJitter > this.base / 2) {
            this.maxJitter = this.base / 2;
        }
        this.maxJitter = maxJitter;
        this.current = base;
    }
    backoff() {
        const ret = this.current + this.maxJitter * Math.random();
        this.current = Math.min(this.current * 2, this.max);
        return ret;
    }
    reset() {
        this.current = this.base;
    }
}
export class ConstantBackoff {
    constructor(value) {
        this.value = value;
    }
    backoff() {
        return this.value;
    }
    reset() {
    }
}
// IntervalRepeater is a Repeater that invokes the task at a fixed interval 
// after the completion of the previous task invocation. If a backoff controller
// is provided, the repeater will use the backoff controller to determine the
// time between invocations after a failure instead. It will reset the backoffController
// on success.
export class IntervalRepeater {
    constructor(interval, backoffController) {
        this.failureCount = 0;
        this.running = false;
        this.interval = interval;
        this.backoffController = backoffController;
    }
    isRunning() {
        return this.running;
    }
    handleSuccess() {
        var _a;
        this.failureCount = 0;
        (_a = this.backoffController) === null || _a === void 0 ? void 0 : _a.reset();
        this.setTimer(this.interval);
    }
    handleFailure() {
        var _a, _b;
        this.failureCount++;
        const time = (_b = (_a = this.backoffController) === null || _a === void 0 ? void 0 : _a.backoff()) !== null && _b !== void 0 ? _b : this.interval;
        this.setTimer(time);
    }
    setTimer(timeout) {
        if (!this.running) {
            return;
        }
        this.timeoutId = setTimeout(this.executeTask.bind(this), timeout);
    }
    executeTask() {
        if (!this.task) {
            return;
        }
        this.task(this.failureCount).then(this.handleSuccess.bind(this), this.handleFailure.bind(this));
    }
    start(immediateExecution) {
        this.running = true;
        if (immediateExecution) {
            scheduleMicrotask(this.executeTask.bind(this));
        }
        else {
            this.setTimer(this.interval);
        }
    }
    stop() {
        this.running = false;
        clearInterval(this.timeoutId);
    }
    reset() {
        var _a;
        this.failureCount = 0;
        (_a = this.backoffController) === null || _a === void 0 ? void 0 : _a.reset();
        this.stop();
    }
    setTask(task) {
        this.task = task;
    }
}
export const __platforms = ['__universal__'];
//# sourceMappingURL=repeater.js.map